import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Circle, CaretDown, X, RocketLaunch } from '@phosphor-icons/react';
import { onboardingAPI } from '../api/api';
import { haptic } from '../utils/haptics';

export const OnboardingChecklist = ({ role }) => {
  const navigate = useNavigate();
  const dismissKey = `onboarding_checklist_dismissed_${role}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(dismissKey) === '1');
  const [collapsed, setCollapsed] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (dismissed) return;
    onboardingAPI.checklist()
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [dismissed]);

  if (dismissed || !data || !data.items.length || data.all_done) return null;

  const pct = Math.round((data.completed / data.total) * 100);

  const dismiss = () => {
    localStorage.setItem(dismissKey, '1');
    setDismissed(true);
  };

  return (
    <div className="mb-6 bg-white dark:bg-[#0F172A] border-4 border-[#0F172A] shadow-[6px_6px_0px_#2563EB]" data-testid="onboarding-checklist">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-3 bg-[#D1FAE5] border-b-4 border-[#0F172A]">
        <div className="w-9 h-9 shrink-0 flex items-center justify-center bg-[#3B82F6] border-2 border-[#0F172A]">
          <RocketLaunch size={18} weight="fill" className="text-[#0F172A]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[#0F172A] font-black uppercase text-sm tracking-tight">Get set up</h3>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-2 bg-white border border-[#0F172A] max-w-[200px]">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[11px] font-bold text-[#5C5C5C]" data-testid="onboarding-progress">
              {data.completed}/{data.total} done
            </span>
          </div>
        </div>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-8 h-8 flex items-center justify-center bg-white hover:bg-[#F0EDE6] text-[#0F172A] border-2 border-[#0F172A] transition-colors"
          aria-label="Toggle checklist"
          data-testid="onboarding-toggle-btn"
        >
          <CaretDown size={14} weight="bold" className={`transition-transform ${collapsed ? '' : 'rotate-180'}`} />
        </button>
        <button
          onClick={dismiss}
          className="w-8 h-8 flex items-center justify-center bg-white hover:bg-[#F0EDE6] text-[#0F172A] border-2 border-[#0F172A] transition-colors"
          aria-label="Dismiss checklist"
          data-testid="onboarding-dismiss-btn"
        >
          <X size={14} weight="bold" />
        </button>
      </div>

      {/* Items */}
      {!collapsed && (
        <div className="divide-y-2 divide-neutral-100 dark:divide-neutral-800">
          {data.items.map((item) => (
            <div key={item.id} className="flex items-center gap-3 px-5 py-3" data-testid={`onboarding-item-${item.id}`}>
              {item.done ? (
                <CheckCircle size={22} weight="fill" className="text-emerald-500 shrink-0" />
              ) : (
                <Circle size={22} weight="bold" className="text-neutral-300 dark:text-neutral-600 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`font-bold text-sm ${item.done ? 'text-neutral-400 line-through' : 'text-neutral-900 dark:text-white'}`}>
                  {item.label}
                </p>
                {!item.done && (
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">{item.hint}</p>
                )}
              </div>
              {!item.done && (
                <button
                  onClick={() => { haptic(8); navigate(item.route); }}
                  className="shrink-0 px-3 py-1.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-[#0F172A] text-[11px] font-black uppercase tracking-wide border-2 border-black shadow-[2px_2px_0px_#0F172A] hover:-translate-y-0.5 transition-all"
                  data-testid={`onboarding-cta-${item.id}`}
                >
                  {item.cta}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OnboardingChecklist;
